import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { openModal } from "../Store/modalSlice";
import ContentCard from "../Layouts/ContentCard";
import LoadingSpiner from "../Components/Loader/LoadingSpiner";
import { usePermissions } from "../hooks/usePermissions";
import {
  useGetUserQuery,
  useGetTenantMembersQuery,
  useUpdateUserRoleMutation,
  useRemoveUserMutation,
  useActivateUserMutation,
  useDeactivateUserMutation,
} from "../api/user";

interface Member {
  id: string;
  email: string;
  status: "Active" | "Inactive" | "Pending";
  role: "Administrator" | "Collaborator" | null;
}

const statusBadgeClass = (status: string) => {
  switch (status) {
    case "Active":
      return "badge-success";
    case "Pending":
      return "badge-warning";
    case "Inactive":
      return "badge-error";
    default:
      return "badge-neutral";
  }
};

// RTK Query error shape is { status, data: { message } }; fall back for network errors.
const getErrorMessage = (error: unknown): string => {
  const data = (error as any)?.data;
  if (data && typeof data === "object" && typeof data.message === "string") {
    return data.message;
  }
  return "Something went wrong. Please try again.";
};

export default function Members() {
  const dispatch = useDispatch();
  const { data: userData, isLoading: userLoading } = useGetUserQuery(undefined);
  const { team } = usePermissions();
  const canManage = team.canManageTeam(userData?.role);

  const { data: members, isLoading: membersLoading, isError: membersError } = useGetTenantMembersQuery(undefined, { skip: !canManage });

  const [updateUserRole] = useUpdateUserRoleMutation();
  const [removeUser] = useRemoveUserMutation();
  const [activateUser] = useActivateUserMutation();
  const [deactivateUser] = useDeactivateUserMutation();

  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  if (userLoading) {
    return (
      <div className="container mx-auto">
        <ContentCard title="Team Members">
          <div className="flex items-center justify-center py-8">
            <LoadingSpiner />
            <span className="ml-2">Loading...</span>
          </div>
        </ContentCard>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="container mx-auto">
        <ContentCard title="Team Members">
          <div className="alert alert-warning">
            <span>{team.getTeamManagementTooltip() || "Team management is not available on your plan."}</span>
          </div>
        </ContentCard>
      </div>
    );
  }

  const runAction = async (action: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await action();
    } catch (err) {
      setActionError(getErrorMessage(err));
    }
  };

  const handleRoleChange = (member: Member, role: string) => {
    runAction(() => updateUserRole({ id: member.id, role }).unwrap());
  };

  const handleToggleActive = (member: Member) => {
    if (member.status === "Active") {
      runAction(() => deactivateUser(member.id).unwrap());
    } else if (member.status === "Inactive") {
      runAction(() => activateUser(member.id).unwrap());
    }
  };

  const handleRemove = (member: Member) => {
    if (confirmRemoveId !== member.id) {
      setConfirmRemoveId(member.id);
      return;
    }
    setConfirmRemoveId(null);
    runAction(() => removeUser(member.id).unwrap());
  };

  const members_: Member[] = members || [];

  return (
    <div className="container mx-auto">
      <ContentCard title="Team Members">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-base-content/70">
            {team.isTeamLimitUnlimited
              ? `${team.currentSeatsUsed} member${team.currentSeatsUsed === 1 ? "" : "s"} (unlimited seats)`
              : `${team.currentSeatsUsed} / ${team.effectiveLimit} seats used`}
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => dispatch(openModal({ type: "invite-send", width: "md", props: {} }))}>
            Invite members
          </button>
        </div>

        {actionError && (
          <div className="alert alert-error mb-4">
            <span>{actionError}</span>
          </div>
        )}

        {membersLoading && (
          <div className="flex items-center justify-center py-8">
            <LoadingSpiner />
          </div>
        )}

        {!membersLoading && membersError && (
          <div className="alert alert-error">
            <span>Unable to load team members. Please try again later.</span>
          </div>
        )}

        {!membersLoading && !membersError && members_.length === 0 && <p className="text-sm text-base-content/70">No team members yet.</p>}

        {!membersLoading && !membersError && members_.length > 0 && (
          <div className="mt-2 overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members_.map((member) => {
                  const isSelf = member.id === userData?.id;
                  const isPending = member.status === "Pending";
                  return (
                    <tr key={member.id}>
                      <td>{member.email}</td>
                      <td>
                        <select
                          className="select select-bordered select-sm"
                          value={member.role || ""}
                          disabled={isSelf || isPending}
                          onChange={(e) => handleRoleChange(member, e.target.value)}
                        >
                          {isPending && <option value="">-</option>}
                          <option value="Administrator">Administrator</option>
                          <option value="Collaborator">Collaborator</option>
                        </select>
                      </td>
                      <td>
                        <span className={`badge ${statusBadgeClass(member.status)}`}>{member.status}</span>
                      </td>
                      <td className="whitespace-nowrap text-right">
                        {!isPending && (
                          <button className="btn btn-ghost btn-xs mr-2" onClick={() => handleToggleActive(member)}>
                            {member.status === "Active" ? "Deactivate" : "Activate"}
                          </button>
                        )}
                        {/* Removing yourself always fails server-side ("Cannot
                            delete active user"), so don't offer it — account
                            closure is the deactivate path. */}
                        <button
                          className={`btn btn-xs ${confirmRemoveId === member.id ? "btn-error" : "btn-error btn-outline"}`}
                          disabled={isSelf}
                          onClick={() => handleRemove(member)}
                        >
                          {confirmRemoveId === member.id ? "Confirm?" : isPending ? "Revoke invite" : "Remove"}
                        </button>
                        {confirmRemoveId === member.id && (
                          <button className="btn btn-ghost btn-xs ml-1" onClick={() => setConfirmRemoveId(null)}>
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </ContentCard>
    </div>
  );
}
